import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  DockerSetupFormValues,
  DockerSetupPolicyPayload,
  DOCKER_SETUP_FORM_INITIAL_VALUES,
  DOCKER_SETUP_INITIAL_VALUES,
} from './types';

/**
 * Type guard to check if data is DockerSetupPolicyPayload
 */
function isDockerSetupPolicyPayload(
  data: unknown
): data is DockerSetupPolicyPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'enableHostManagementFeatures' in data
  );
}

/**
 * Hook to get initial values for Docker Setup Policy form.
 *
 * Returns complete form values including common fields (name, environmentGroupIds)
 * and type-specific fields.
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Complete form initial values
 */
export function useDockerSetupInitialValues(
  policy?: TypedPolicy<unknown>
): DockerSetupFormValues {
  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return DOCKER_SETUP_FORM_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isDockerSetupPolicyPayload(data)) {
      return {
        // Common fields from policy metadata
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        // Type-specific defaults (using type-specific only, not full form values)
        ...DOCKER_SETUP_INITIAL_VALUES,
      };
    }

    return {
      // Common fields from policy metadata
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      // Type-specific fields from policy data
      enableHostManagementFeatures:
        data.enableHostManagementFeatures?.value ?? false,
      allowVolumeBrowserForRegularUsers:
        data.allowVolumeBrowserForRegularUsers?.value ?? false,
      enableGpuManagement: data.enableGpuManagement?.value ?? false,
      enableImageNotification: data.enableImageNotification?.value ?? false,
      gpus: data.gpus?.value ?? [],
      changeWindow: data.endpointChangeWindow?.value ?? {
        Enabled: false,
        StartTime: '22:00',
        EndTime: '02:00',
      },
    };
  }, [policy]);
}
